import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import Paper from "@mui/material/Paper";
import { ChangeEvent, useState } from "react";
import { Vendor } from "../../__generated__/graphql";
import { UPDATE_VENDOR_ITEMS } from "./vendor/graphql";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { allergens } from "./dish-backup";
import { ButtonContainer } from "../../components/ButtonContainer";
import { ErrorRenderer } from "../../components/ErrorRenderer";

export const allergensList = [
  "celery",
  "cereals",
  "crustaceans",
  "eggs",
  "fish",
  "lupin",
  "milk",
  "molluscs",
  "mustard",
  "peanuts",
  "sesame",
  "soybeans",
  "sulphur dioxide/sulphites",
  "tree nuts",
  "wheat",
];

const schema = yup
  .object({
    vendorItems: yup.array().of(
      yup
        .object({
          order: yup.number().nullable(),
          name: yup.string().required(),
          description: yup.string().optional(),
          price: yup
            .number()
            .transform((_, val) => (val ? Number(val) : null))
            .nullable(),
          allergens: yup
            .array()
            .of(
              yup.object({
                name: yup.string().required(),
                included: yup.boolean().required(),
              })
            )
            .required(),
        })
        .required()
    ),
  })
  .required();

interface IItem {
  order: number;
  name?: string;
  description?: string;
  price?: number;
  allergens?: { name: string; included: boolean }[];
}

export const Dish = ({ vendor }: { vendor: Vendor }) => {
  const [forDeletion, updateForDeletion] = useState<string[]>([]);
  const items: IItem[] =
    Object.keys(vendor.items ?? {}).map((key) => {
      const item = vendor.items[key];
      return {
        ...item,
        allergens: item.allergens as { name: string; included: boolean }[],
      };
    }).sort((a,b)=> a.order - b.order) ?? [];

  const defaultAllergens = allergensList.map((item) => {
    return {
      name: item,
      included: false,
    };
  });

  const defaultItem = {
    name: "",
    description: "",
    price: 0,
    allergens: defaultAllergens,
  };

  const [updateVendor, { loading: updateLoading, error }] = useMutation(
    UPDATE_VENDOR_ITEMS,
    {
      onCompleted: () => {
        toast("Dishes updated");
      },
    }
  );

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, dirtyFields },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      vendorItems: items || [defaultItem],
    },
  });

  const { fields, replace, append, remove, swap, move, insert } = useFieldArray(
    {
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: "vendorItems", // unique name for your Field Array
    }
  );

  const onSubmit = ({ vendorItems }: { vendorItems: IItem[] }) => {
    const withoutDeleted = vendorItems
      .sort((a, b) => a.order - b.order)
      .filter((item: IItem) => !forDeletion.includes(item?.name!));

    const mapped = withoutDeleted.reduce((acc, next) => {
      const { name, order } = next;

      // @ts-ignore
      acc[`${order}-${name}`] = {
        ...next,
      };
      return acc;
    }, {});

    updateVendor({
      variables: {
        vendor: {
          id: vendor.id,
          items: mapped,
        },
      },
    });
  };
  const hasItems = fields.length > 0;

  return (
    // @ts-ignore
    <form onSubmit={handleSubmit(onSubmit)}>
      <h3>Dishes</h3>
      <Button
        onClick={() =>
          append({
            ...defaultItem,
            // @ts-ignore
            keyId: "",
          })
        }
        color="success"
        variant="contained"
        style={{ height: "100%" }}
      >
        Add New
      </Button>
      <h3>Checks indicate that the allergen can be found within the dish.</h3>
      <div>
        {!hasItems && <p>No dishes found</p>}

        {hasItems && (
          <div style={{ display: "flex", flex: 1 }}>
            <TableContainer
              component={Paper}
              style={{
                minHeight: 500,
                maxHeight: 500,
                borderTop: "1px solid lightgrey",
                marginTop: 8,
                overflow: "auto", // Add this to ensure scrolling works
              }}
            >
              <Table
                sx={{ minWidth: 650 }}
                aria-label="simple table"
                stickyHeader
              >
                <TableHead
                // style={{position: 'sticky', top: 0}}
                >
                  <TableRow style={{ alignItems: "end" }}>
                    <TableCell
                      style={{
                        borderRight: "1px solid lightgrey",
                        minWidth: 50,
                      }}
                    >
                      Order
                    </TableCell>
                    <TableCell
                      style={{
                        borderRight: "1px solid lightgrey",
                        minWidth: 150,
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      style={{
                        borderRight: "1px solid lightgrey",
                        minWidth: 200,
                      }}
                    >
                      description
                    </TableCell>
                    <TableCell
                      style={{
                        borderRight: "1px solid lightgrey",
                        width: 120,
                        maxWidth: 120,
                        minWidth: 120,
                      }}
                    >
                      Price
                    </TableCell>
                    {allergensList.map((item) => {
                      return (
                        <TableCell
                          style={{
                            height: 150,
                            maxWidth: 20,
                            width: 30,
                            overflow: "hidden",
                            wordWrap: "normal",
                            // flexWrap: 'wrap'
                            // textWrap: "wrap"
                            paddingLeft: 0,
                            textAlign: "center",
                            borderRight: "1px solid lightgrey",
                          }}
                          align="left"
                        >
                          <div
                            style={{
                              textTransform: "capitalize",
                              transform: `rotate(310deg)`,
                            }}
                          >
                            {item}
                          </div>
                        </TableCell>
                      );
                    })}
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((row, index) => {
                    return (
                      <TableRow>
                        <TableCell
                          style={{ borderRight: "1px solid lightgrey" }}
                        >
                          <TextField
                            id="outlined-basic"
                            label="order"
                            fullWidth
                            variant="outlined"
                            defaultValue={row.order}
                            {...register(`vendorItems.${index}.order`)}
                          />
                        </TableCell>
                        <TableCell
                          style={{ borderRight: "1px solid lightgrey" }}
                        >
                          <TextField
                            id="outlined-basic"
                            label="name"
                            fullWidth
                            variant="outlined"
                            defaultValue={row.name}
                            {...register(`vendorItems.${index}.name`)}
                          />
                        </TableCell>
                        <TableCell
                          style={{ borderRight: "1px solid lightgrey" }}
                        >
                          <TextField
                            id="outlined-basic"
                            fullWidth
                            label="description"
                            variant="outlined"
                            defaultValue={row.description}
                            {...register(`vendorItems.${index}.description`)}
                          />
                        </TableCell>
                        <TableCell
                          style={{ borderRight: "1px solid lightgrey" }}
                        >
                          <TextField
                            id="outlined-basic"
                            label="price"
                            variant="outlined"
                            defaultValue={row.price}
                            {...register(`vendorItems.${index}.price`)}
                          />
                        </TableCell>
                        {allergensList.map((defaultAllergen, ai) => {
                          const allergen = row.allergens.find(
                            (al) => al.name === defaultAllergen
                          ) || {
                            name: defaultAllergen,
                            included: false,
                          };

                          return (
                            <TableCell
                              style={{ borderRight: "1px solid lightgrey" }}
                            >
                              <Controller
                                control={control}
                                {...register(
                                  `vendorItems.${index}.allergens.${ai}`
                                )}
                                render={({
                                  field: { onChange, onBlur, value, ref },
                                }) => {
                                  // if allergens have has been updated, use default false.
                                  if (!value) {
                                    setValue(
                                      `vendorItems.${index}.allergens.${ai}`,
                                      { name: defaultAllergen, included: false }
                                    );
                                  }

                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Checkbox
                                        id="outlined-basic"
                                        onChange={(
                                          event: ChangeEvent<HTMLInputElement>,
                                          checked: boolean
                                        ) => {
                                          setValue(
                                            `vendorItems.${index}.allergens.${ai}.included`,
                                            checked
                                          );
                                        }}
                                        defaultChecked={allergen.included}
                                        value={value?.included ?? false}
                                      />
                                    </div>
                                  );
                                }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <Button
                            disabled={forDeletion.includes(row.name)}
                            onClick={() => {
                              console.log("item", row);
                              // @ts-ignore
                              if (row.keyId === "") {
                                remove(index);
                              } else {
                                updateForDeletion([...forDeletion, row.name]);
                              }
                            }}
                            color="error"
                            variant="contained"
                            style={{
                              // marginTop: 18,
                              marginBottom: 20,
                              // marginRight: 8,
                            }}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <ErrorRenderer errors={errors} />
            </TableContainer>
          </div>
        )}
      </div>
      <Button
        type="submit"
        variant="contained"
        style={{ marginTop: 18, marginBottom: 18 }}
      >
        Save
      </Button>
    </form>
  );
};
